import type { ChannelChannel, ChannelEditOp, ChannelFloor, ChannelSample, ChannelTree } from '@/api'

export class ChannelProjectionError extends Error {}
class ChannelProjectionTargetMissingError extends ChannelProjectionError {}

function cloneTree(tree: ChannelTree): ChannelTree {
  return JSON.parse(JSON.stringify(tree)) as ChannelTree
}

function stableId(prefix: string, requested: string | null | undefined, op: ChannelEditOp): string {
  if (requested) return requested
  if (op.save_id) return `${prefix}-${op.save_id.replaceAll('-', '').slice(0, 12)}`
  throw new ChannelProjectionError(`${op.op} needs a stable ${prefix}_id`)
}

function floorIdentity(value: string | null | undefined): string | null {
  const match = /^([+-]?)([0-9]+)$/.exec(value ?? '')
  if (!match) return null
  const magnitude = match[2].replace(/^0+/, '') || '0'
  return `${match[1] === '-' && magnitude !== '0' ? '-' : '+'}${magnitude}`
}

function compareFloors(left: ChannelFloor, right: ChannelFloor): number {
  const category = (floor: ChannelFloor) =>
    floor.floor == null ? 2 : /^[+-]?[0-9]+$/.test(floor.floor) ? 0 : 1
  const leftCategory = category(left)
  const rightCategory = category(right)
  if (leftCategory !== rightCategory) return leftCategory - rightCategory
  if (leftCategory !== 0) return 0
  const leftIdentity = floorIdentity(left.floor)!
  const rightIdentity = floorIdentity(right.floor)!
  const leftNegative = leftIdentity.startsWith('-')
  const rightNegative = rightIdentity.startsWith('-')
  if (leftNegative !== rightNegative) return leftNegative ? -1 : 1
  const leftDigits = leftIdentity.slice(1)
  const rightDigits = rightIdentity.slice(1)
  if (leftDigits === rightDigits) return 0
  const magnitude =
    leftDigits.length !== rightDigits.length
      ? leftDigits.length - rightDigits.length
      : leftDigits.localeCompare(rightDigits)
  return leftNegative ? -magnitude : magnitude
}

type NaturalPart = { kind: 'number'; digits: string } | { kind: 'text'; text: string }

function naturalParts(value: string): NaturalPart[] {
  return value
    .split(/(\d+)/)
    .filter(Boolean)
    .map((part) =>
      /^\d+$/.test(part)
        ? { kind: 'number', digits: part.replace(/^0+/, '') || '0' }
        : { kind: 'text', text: part.toLowerCase() },
    )
}

function compareOrdinal(left: string, right: string): number {
  const a = Array.from(left)
  const b = Array.from(right)
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    if (a[index] === undefined) return -1
    if (b[index] === undefined) return 1
    const leftCodePoint = a[index]!.codePointAt(0)!
    const rightCodePoint = b[index]!.codePointAt(0)!
    if (leftCodePoint !== rightCodePoint) return leftCodePoint - rightCodePoint
  }
  return 0
}

function compareNatural(left: string, right: string): number {
  const a = naturalParts(left)
  const b = naturalParts(right)
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    if (a[index] === undefined) return -1
    if (b[index] === undefined) return 1
    const leftPart = a[index]!
    const rightPart = b[index]!
    if (leftPart.kind !== rightPart.kind) return leftPart.kind === 'number' ? -1 : 1
    if (leftPart.kind === 'number' && rightPart.kind === 'number') {
      if (leftPart.digits.length !== rightPart.digits.length) {
        return leftPart.digits.length - rightPart.digits.length
      }
      if (leftPart.digits !== rightPart.digits) {
        return leftPart.digits < rightPart.digits ? -1 : 1
      }
    }
    if (leftPart.kind === 'text' && rightPart.kind === 'text' && leftPart.text !== rightPart.text) {
      return compareOrdinal(leftPart.text, rightPart.text)
    }
  }
  return 0
}

function compareChannels(left: ChannelChannel, right: ChannelChannel): number {
  const leftSamples = (left.samples ?? []).map((sample) => sample.sample_no).sort(compareNatural)
  const rightSamples = (right.samples ?? []).map((sample) => sample.sample_no).sort(compareNatural)
  if (!leftSamples.length || !rightSamples.length) {
    if (Boolean(leftSamples.length) !== Boolean(rightSamples.length)) {
      return leftSamples.length ? -1 : 1
    }
  } else {
    const lowest = compareNatural(leftSamples[0], rightSamples[0])
    if (lowest) return lowest
    for (let index = 0; index < Math.max(leftSamples.length, rightSamples.length); index += 1) {
      if (leftSamples[index] === undefined) return -1
      if (rightSamples[index] === undefined) return 1
      const compared = compareNatural(leftSamples[index], rightSamples[index])
      if (compared) return compared
    }
  }
  return compareOrdinal(left.channel_id ?? '', right.channel_id ?? '')
}

function findFloor(tree: ChannelTree, op: ChannelEditOp): ChannelFloor | undefined {
  if (op.floor_diagram_id)
    return tree.floors?.find((floor) => floor.floor_diagram_id === op.floor_diagram_id)
  const matches = tree.floors?.filter((floor) => floor.floor === op.floor) ?? []
  if (matches.length > 1) throw new ChannelProjectionError(`floor selector is ambiguous`)
  return matches[0]
}

function findChannel(
  tree: ChannelTree,
  channelId: string | null | undefined,
): { floor: ChannelFloor; channel: ChannelChannel } | null {
  for (const floor of tree.floors ?? []) {
    const channel = floor.channels?.find((candidate) => candidate.channel_id === channelId)
    if (channel) return { floor, channel }
  }
  return null
}

function findSample(
  tree: ChannelTree,
  sampleId: string | null | undefined,
): { channel: ChannelChannel; index: number; sample: ChannelSample } | null {
  for (const floor of tree.floors ?? []) {
    for (const channel of floor.channels ?? []) {
      const index = channel.samples?.findIndex((sample) => sample.sample_id === sampleId) ?? -1
      if (index >= 0) return { channel, index, sample: channel.samples![index] }
    }
  }
  return null
}

function insertAfter<T extends Record<string, unknown>>(
  items: T[],
  item: T,
  afterId: string | null | undefined,
  key: keyof T,
): void {
  if (!afterId) {
    items.push(item)
    return
  }
  const index = items.findIndex((candidate) => candidate[key] === afterId)
  if (index < 0) throw new ChannelProjectionTargetMissingError(`after_id ${afterId} was not found`)
  items.splice(index + 1, 0, item)
}

function decimalParts(value: string): { value: bigint; scale: number } | null {
  const match = /^([0-9]+)(?:\.([0-9]+))?$/.exec(value.trim())
  if (!match) return null
  const fraction = match[2] ?? ''
  const parsed = BigInt(`${match[1]}${fraction}`)
  if (parsed <= 0n) return null
  return { value: parsed, scale: fraction.length }
}

function decimalText(value: bigint, scale: number): string {
  if (!scale) return value.toString()
  const digits = value.toString().padStart(scale + 1, '0')
  const rendered = `${digits.slice(0, -scale)}.${digits.slice(-scale)}`.replace(/\.?0+$/, '')
  return rendered || '0'
}

function rederive(channel: ChannelChannel): void {
  const samples = channel.samples ?? []
  const parsed = samples.map((sample) => decimalParts(sample.width ?? ''))
  if (parsed.some((width) => width === null)) {
    channel.from = samples[0]?.frm ?? ''
    channel.to = samples.at(-1)?.to ?? ''
    return
  }
  const scale = Math.max(0, ...parsed.map((width) => width!.scale))
  let cursor = 0n
  for (let index = 0; index < samples.length; index += 1) {
    const width = parsed[index]!
    samples[index].frm = decimalText(cursor, scale)
    cursor += width.value * 10n ** BigInt(scale - width.scale)
    samples[index].to = decimalText(cursor, scale)
  }
  channel.from = samples.length ? '0' : ''
  channel.to = samples.length ? decimalText(cursor, scale) : ''
}

function canonicalize(tree: ChannelTree): ChannelTree {
  tree.floors ??= []
  tree.floors.sort(compareFloors)
  const channels = tree.floors.flatMap((floor) => {
    floor.channels ??= []
    floor.channels.sort(compareChannels)
    return floor.channels
  })
  channels.forEach((channel, index) => {
    channel.chn_id = `Channel ${index + 1}`
  })
  channels.forEach(rederive)
  return tree
}

type EditHandler = (tree: ChannelTree, op: ChannelEditOp) => void

const addFloor: EditHandler = (tree, op) => {
  if (op.floor == null) throw new ChannelProjectionError('add_floor needs floor')
  const identity = floorIdentity(op.floor)
  if (
    op.diagram_page == null &&
    tree.floors!.some(
      (floor) => floor.floor === op.floor || (identity && floorIdentity(floor.floor) === identity),
    )
  ) {
    throw new ChannelProjectionError('floor already exists')
  }
  const floor: ChannelFloor = {
    floor_diagram_id: stableId('floor', op.floor_diagram_id, op),
    floor: op.floor,
    channels: [],
  }
  if (op.chn_id) {
    floor.channels!.push({
      channel_id: stableId('channel', op.channel_id, op),
      chn_id: '',
      samples: [],
    })
  }
  if (Object.hasOwn(op, 'diagram_page')) {
    floor.diagram_page = op.diagram_page
    floor.diagram_bounding_box = op.diagram_bounding_box ?? null
  }
  insertAfter(tree.floors!, floor, op.after_id, 'floor_diagram_id')
}

const removeFloor: EditHandler = (tree, op) => {
  const floor = findFloor(tree, op)
  if (!floor) throw new ChannelProjectionTargetMissingError('floor not found')
  if (floor.channels?.length && !op.cascade) {
    throw new ChannelProjectionError('cannot remove a floor that still has channels')
  }
  tree.floors!.splice(tree.floors!.indexOf(floor), 1)
}

const addChannel: EditHandler = (tree, op) => {
  if (findChannel(tree, op.channel_id)) throw new ChannelProjectionError('channel already exists')
  const floor = findFloor(tree, op)
  if (!floor) throw new ChannelProjectionTargetMissingError('target floor not found')
  floor.channels ??= []
  insertAfter(
    floor.channels,
    { channel_id: stableId('channel', op.channel_id, op), chn_id: '', samples: [] },
    op.after_id,
    'channel_id',
  )
}

const removeChannel: EditHandler = (tree, op) => {
  const found = findChannel(tree, op.channel_id)
  if (!found) throw new ChannelProjectionTargetMissingError('channel not found')
  if (found.channel.samples?.length && !op.cascade) {
    throw new ChannelProjectionError('cannot remove a channel that still has samples')
  }
  found.floor.channels!.splice(found.floor.channels!.indexOf(found.channel), 1)
}

const setChannelFloor: EditHandler = (tree, op) => {
  const found = findChannel(tree, op.channel_id)
  const destination = findFloor(tree, op)
  if (!found || !destination) {
    throw new ChannelProjectionTargetMissingError('target floor not found')
  }
  found.floor.channels!.splice(found.floor.channels!.indexOf(found.channel), 1)
  destination.channels ??= []
  insertAfter(destination.channels, found.channel, op.after_id, 'channel_id')
}

const setFloorLabel: EditHandler = (tree, op) => {
  const floor = findFloor(tree, op)
  if (!floor || op.floor == null) throw new ChannelProjectionTargetMissingError('floor not found')
  const identity = floorIdentity(op.floor)
  if (
    tree.floors!.some(
      (candidate) =>
        candidate !== floor &&
        (candidate.floor === op.floor || (identity && floorIdentity(candidate.floor) === identity)),
    )
  ) {
    throw new ChannelProjectionError('floor already exists')
  }
  floor.floor = op.floor
}

const reassignSample: EditHandler = (tree, op) => {
  const source = findSample(tree, op.sample_id)
  const destination = findChannel(tree, op.channel_id)
  if (!source || !destination) {
    throw new ChannelProjectionTargetMissingError('unknown sample or channel')
  }
  source.channel.samples!.splice(source.index, 1)
  destination.channel.samples ??= []
  insertAfter(destination.channel.samples, source.sample, op.after_id, 'sample_id')
}

const setSampleFields: EditHandler = (tree, op) => {
  const found = findSample(tree, op.sample_id)
  if (!found) throw new ChannelProjectionTargetMissingError('sample not found')
  if (op.sample_no != null) found.sample.sample_no = op.sample_no
  if (op.width != null) found.sample.width = op.width
  if (op.note != null) found.sample.note = op.note
  if (op.page != null) found.sample.page = op.page
}

const addSample: EditHandler = (tree, op) => {
  const destination = findChannel(tree, op.channel_id)
  const sampleNumbers = op.sample_nos ?? (op.sample_no ? [op.sample_no] : [])
  if (!destination) {
    throw new ChannelProjectionTargetMissingError('add_sample target channel not found')
  }
  if (!sampleNumbers.length || !op.save_id) {
    throw new ChannelProjectionError('add_sample needs a target channel and sample')
  }
  destination.channel.samples ??= []
  sampleNumbers.forEach((sampleNo, index) => {
    const sampleId =
      sampleNumbers.length === 1
        ? stableId('sample', op.sample_id, op)
        : `sample-${op.save_id!.replaceAll('-', '').slice(0, 12)}-${String(index + 1).padStart(3, '0')}`
    insertAfter(
      destination.channel.samples!,
      {
        sample_id: sampleId,
        sample_no: sampleNo,
        width: op.width ?? '',
        note: op.note ?? '',
        page: op.page ?? '',
      },
      op.after_id,
      'sample_id',
    )
  })
  destination.channel.samples.sort((left, right) => compareNatural(left.sample_no, right.sample_no))
}

const removeSample: EditHandler = (tree, op) => {
  const found = findSample(tree, op.sample_id)
  if (!found) throw new ChannelProjectionTargetMissingError('sample not found')
  found.channel.samples!.splice(found.index, 1)
}

const setFloorLocation: EditHandler = (tree, op) => {
  if (!op.floor_diagram_id) throw new ChannelProjectionError('Select a diagram occurrence to edit.')
  const floor = findFloor(tree, op)
  if (!floor) throw new ChannelProjectionTargetMissingError('floor not found')
  floor.diagram_page = op.diagram_page ?? null
  floor.diagram_bounding_box = op.diagram_bounding_box ?? null
}

const editHandlers: Partial<Record<ChannelEditOp['op'], EditHandler>> = {
  add_floor: addFloor,
  remove_floor: removeFloor,
  add_channel: addChannel,
  remove_channel: removeChannel,
  set_channel_floor: setChannelFloor,
  set_floor_label: setFloorLabel,
  set_floor_location: setFloorLocation,
  reassign_sample: reassignSample,
  set_sample_fields: setSampleFields,
  add_sample: addSample,
  remove_sample: removeSample,
}

function applyEdit(tree: ChannelTree, op: ChannelEditOp): void {
  tree.floors ??= []
  if (op.op === 'set_channel_label') {
    throw new ChannelProjectionError('CHN_ID is system-owned and cannot be changed')
  }
  if (op.op === 'clear_floors') {
    tree.floors = []
    return
  }
  editHandlers[op.op]?.(tree, op)
}

export function projectChannelEdits(
  tree: ChannelTree,
  edits: ChannelEditOp[],
  { ignoreMissingTargets = false } = {},
): ChannelTree {
  const projected = canonicalize(cloneTree(tree))
  edits.forEach((edit) => {
    try {
      applyEdit(projected, edit)
    } catch (error) {
      if (!ignoreMissingTargets || !(error instanceof ChannelProjectionTargetMissingError)) {
        throw error
      }
    }
  })
  return canonicalize(projected)
}
